import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  useCreatePromocion,
  useFetchAllCountries,
  useFetchCanalVentaAll,
  useFetchCentrosCostos,
  useFetchCoberturasCiudadEnabled,
  useFetchCoberturasProvincia,
  useFetchCoberturasSectorEnabled,
  useFetchCoberturasZonaEnabled,
  useFetchPlans,
  useUpdatePromocion,
} from '@/shared/actions/erp';
import {
  CustomAutocompleteArrString,
  CustomNumberTextField,
  CustomTextField,
  SampleCheckbox,
  SampleDatePicker,
  SampleRadioButtonsGroup,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import {
  CENTROCOSTO,
  CanalVenta,
  City,
  Country,
  IPromocion,
  Plan,
  Province,

} from '@/shared/interfaces';
import { newPromocionSchema } from '@/shared/utils';

import {
  MES_GRATIS_STRING,
  RECURRENCIAS_CHOICE,
  ROUTER_PATHS,
  TipoDescuentoEnumChoice,
  gridSizeMdLg10,
  gridSizeMdLg2,
  gridSizeMdLg6,
  useLoaders,
} from '@/shared';
import { Grid, InputAdornment } from '@mui/material';
import { Sector, Zone } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import AcutocompletePromocion from './AcutocompletePromocion';
import CustomAutocompleteMultipleArrString from '@/shared/components/ui/CustomAutocompleteMultipleArrString';

export type SavePromocionProps = {
  editPromocion?: IPromocion;
  titlePage: string;
  isEditing?: boolean;
};

export type CreateFormData = Partial<IPromocion> & {
  nombre: string;
  descripcion?: string;
  fecha_inicio: Date;
  fecha_fin: Date;
  estado: boolean;
  recurrencia: string;
  porcentaje_descuento: number;
  descuento_precio: number;

  // selectAll
  allCountries: boolean;
  allProvinces: boolean;
  allCities: boolean;
  allZonas: boolean;
  allSectores: boolean;
  allCentrosCosto: boolean;
  allCanalesVenta: boolean;

  allPlanes: boolean;
  allTiposServicio: boolean;
  allMetodosPago: boolean;
  allTiposCliente: boolean;
};

const SavePromocion: React.FC<SavePromocionProps> = ({
  editPromocion,
  titlePage,
  // isEditing,
}) => {
  const navigate = useNavigate();
  const returnUrlPromocionesPage =
  ROUTER_PATHS.administration.promocionesNav;
  ///* local state ---------------------
  const [shrink, setShrink] = useState(false);
  const [minDate, setMinDate] = useState<any | null>(null);

  /// all values
  const allDataArr = ['*'];

  ///* form ---------------------
  const form = useForm<CreateFormData>({
    resolver: yupResolver(newPromocionSchema) as any,
    defaultValues: {
      tipo_descuento: TipoDescuentoEnumChoice.PORCENTAJE,
      valor_descuento: 0 as any,
      prioridad: 1,
      estado: true,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const watchedTipoDescuento = form.watch('tipo_descuento');
  const watchedAllCountries = form.watch('allCountries');
  const watchedAllProvinces = form.watch('allProvinces');
  const watchedAllCities = form.watch('allCities');
  const watchedAllZonas = form.watch('allZonas');
  const watchedAllSectores = form.watch('allSectores');
  const watchedAllCentrosCosto = form.watch('allCentrosCosto');
  const watchedAllCanalesVenta = form.watch('allCanalesVenta');
  const watchedAllPlanes = form.watch('allPlanes');

  ///* fetch data ---------------------
  const {
    data: paisesPagingRes,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 100,
  });
  const {
    data: provinciasPagingRes,
    isLoading: isLoadingProvicias,
    isRefetching: isRefetchingProvincias,
  } = useFetchCoberturasProvincia({
    page_size: 1000,
  });
  const {
    data: citiesPagingRes,
    isLoading: isLoadingCities,
    isRefetching: isRefetchingCities,
  } = useFetchCoberturasCiudadEnabled({
    enabled: true,
    params: {
      page_size: 600,
    },
  });
  const {
    data: zonasPagingRes,
    isLoading: isLoadingZonas,
    isRefetching: isRefetchingZonas,
  } = useFetchCoberturasZonaEnabled({
    enabled: true,
    params: {
      page_size: 1000,
    },
  });
  const {
    data: sectoresPagingRes,
    isLoading: isLoadingSectores,
    isRefetching: isRefetchingSectores,
  } = useFetchCoberturasSectorEnabled({
    enabled: true,
    params: {
      page_size: 1000,
    },
  });

  const {
    data: centroCostoRes,
    isLoading: isLoadingCentroCosto,
    isRefetching: isRefetchingCentroCosto,
  } = useFetchCentrosCostos({
    page_size: 1000,
  });
  const {
    data: canalesVentaRes,
    isLoading: isLoadingCanalesVenta,
    isRefetching: isRefetchingCanalesVenta,
  } = useFetchCanalVentaAll({
    params: {
      page_size: 600,
    },
  });
  const {
    data: planesRes,
    isLoading: isLoadingPlanes,
    isRefetching: isRefetchingPlanes,
  } = useFetchPlans({
    page_size: 1000,
  });

  // TODO: ============================
  // const { data: tiposClienteRes, isLoading: isLoadingTiposCliente } =
  //   useFetchTiposCliente({
  //     page_size: 1000,
  //   });

  ///* mutations ---------------------
  const createPromocion = useCreatePromocion({
    navigate,
    returnUrl: returnUrlPromocionesPage,
  });
  const updatePromocion = useUpdatePromocion({
    navigate,
    returnUrl: returnUrlPromocionesPage,
  });

  ///* handlers ---------------------
  const onSave = async (data: CreateFormData) => {
    if (!isValid) return;

    ///* update
    if (editPromocion?.id_promocion) {
      updatePromocion.mutate({
        id: editPromocion.id_promocion,
        data: {
          ...data,
          fecha_inicio: dayjs(data.fecha_inicio).format('YYYY-MM-DD'),
          fecha_fin: !!data.fecha_fin
            ? dayjs(data.fecha_fin).format('YYYY-MM-DD')
            : null,
        } as any,
      });
      return;
    }

    ///* create
    createPromocion.mutate({
      ...data,
      fecha_inicio: dayjs(data.fecha_inicio).format('YYYY-MM-DD'),
      fecha_fin: !!data.fecha_fin
        ? dayjs(data.fecha_fin).format('YYYY-MM-DD')
        : null,
    } as any);
  };

  ///* effects ---------------------
  useEffect(() => {
    if (!editPromocion?.id_promocion) return;
    setShrink(true);

    reset({
      ...editPromocion,
      // fecha_inicio: dayjs(editPromocion.fecha_inicio).toDate(),

      allCountries: !!editPromocion?.paises?.includes('*'),
      allProvinces: !!editPromocion?.provincias?.includes('*'),
      allCities: !!editPromocion?.ciudades?.includes('*'),
      allZonas: !!editPromocion?.zonas?.includes('*'),
      allSectores: !!editPromocion?.sectores?.includes('*'),
      allPlanes: !!editPromocion?.planes?.includes('*'),
      allCentrosCosto: !!editPromocion?.centros_costo?.includes('*'),
      allCanalesVenta: !!editPromocion?.canales_venta?.includes('*'),
    } as unknown as CreateFormData);

    !!form.getValues().fecha_inicio &&
      setMinDate(dayjs(form.getValues().fecha_inicio));
  }, [editPromocion, reset]);

  useLoaders(
    isLoadingCountries ||
      isLoadingProvicias ||
      isLoadingCities ||
      isLoadingZonas ||
      isLoadingSectores ||
      isLoadingCentroCosto ||
      isLoadingCanalesVenta ||
      isRefetchingCanalesVenta ||
      isRefetchingCentroCosto ||
      isRefetchingCities ||
      isRefetchingCountries ||
      isRefetchingProvincias ||
      isRefetchingSectores ||
      isRefetchingZonas ||
      isLoadingPlanes ||
      isRefetchingPlanes
  );

  if (
    isLoadingCities ||
    isLoadingZonas ||
    isLoadingSectores
    // isLoadingServicesType ||
    // isLoadingCentroCosto ||
    // isLoadingCanalesVenta ||
    // isLoadingTiposCliente ||
  )
    return null;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlPromocionesPage);
      }}
      onSave={handleSubmit(onSave)}
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />
     {/*  <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        defaultValue={form.getValues().codigo || ''}
        error={errors.codigo}
        helperText={errors.codigo?.message}
        shrink={shrink}
      /> */}

      {/* ============ tipo descuento ============ */}
      <>
        <SampleRadioButtonsGroup
          label="Tipo descuento"
          control={form.control}
          name="tipo_descuento"
          defaultValue={form.getValues().tipo_descuento || ''}
          options={[
            {
              value: TipoDescuentoEnumChoice.PORCENTAJE,
              label: 'Porcentaje',
            },
            { value: TipoDescuentoEnumChoice.VALOR, label: 'Precio' },
          ]}
        />
        <CustomNumberTextField
          label={`${
            watchedTipoDescuento === TipoDescuentoEnumChoice.VALOR
              ? 'Valor'
              : 'Porcentaje'
          } de descuento`}
          name="valor_descuento"
          key="valor_descuento"
          control={form.control}
          defaultValue={form.getValues().valor_descuento || ''}
          error={errors.valor_descuento}
          helperText={errors.valor_descuento?.message}
          shrink={shrink}
          min={0}
          max={
            watchedTipoDescuento === TipoDescuentoEnumChoice.VALOR
              ? 999999
              : 100
          }
          startAdornment={
            <InputAdornment position="start">
              {watchedTipoDescuento === TipoDescuentoEnumChoice.VALOR
                ? '$'
                : '%'}
            </InputAdornment>
          }
        />
      </>

      <CustomNumberTextField
        label="Prioridad"
        name="prioridad"
        key="prioridad"
        control={form.control}
        defaultValue={form.getValues().prioridad || ''}
        error={errors.prioridad}
        helperText={errors.prioridad?.message}
        shrink={shrink}
        min={1}
      />

      {/* ============ select check | JSON ============ */}
      <>
        {/* ------ Pais ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<Country>
            label="Paises"
            name="paises"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={paisesPagingRes?.data?.data || []}
            defaultValue={
              !!editPromocion?.paises?.length
                ? paisesPagingRes?.data?.data.filter((country: Country) => {
                    const cantonesDb = editPromocion?.paises || [];

                    return cantonesDb.some(
                      (countryDb: string) => countryDb === country?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingCountries}
            // errors
            control={form.control}
            error={!!errors.paises?.length}
            helperText={errors.paises?.message}
            disabled={!!watchedAllCountries}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allCountries"
            control={form.control}
            defaultValue={!!form.getValues().allCountries}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allCountries: value,
                paises: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Provincia ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<Province>
            label="Provincias"
            name="provincias"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={provinciasPagingRes?.data || []}
            defaultValue={
              !!editPromocion?.provincias?.length
                ? provinciasPagingRes?.data?.filter((province: Province) => {
                    const provinciasDb = editPromocion?.provincias || [];

                    return provinciasDb.some(
                      (provinceDb: string) => provinceDb === province?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingProvicias}
            // errors
            control={form.control}
            error={!!errors.provincias?.length}
            helperText={errors.provincias?.message}
            disabled={!!watchedAllProvinces}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allProvinces"
            control={form.control}
            defaultValue={!!form.getValues().allProvinces}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allProvinces: value,
                provincias: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Ciudad ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<City>
            label="Ciudades"
            name="ciudades"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={citiesPagingRes?.data?.data || []}
            defaultValue={
              !!editPromocion?.ciudades?.length
                ? citiesPagingRes?.data?.data.filter((city: City) => {
                    const ciudadesDb = editPromocion?.ciudades || [];

                    return ciudadesDb.some(
                      (cityDb: string) => cityDb === city?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingCities}
            // errors
            control={form.control}
            error={!!errors.ciudades?.length}
            helperText={errors.ciudades?.message}
            disabled={!!watchedAllCities}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allCities"
            control={form.control}
            defaultValue={!!form.getValues().allCities}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allCities: value,
                ciudades: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Zonas ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<Zone>
            label="Zonas"
            name="zonas"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={zonasPagingRes?.data?.data || []}
            defaultValue={
              !!editPromocion?.zonas?.length
                ? zonasPagingRes?.data?.data.filter((zone: Zone) => {
                    const zonasDb = editPromocion?.zonas || [];

                    return zonasDb.some(
                      (zoneDb: string) => zoneDb === zone?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingZonas}
            // errors
            control={form.control}
            error={!!errors.zonas?.length}
            helperText={errors.zonas?.message}
            disabled={!!watchedAllZonas}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allZonas"
            control={form.control}
            defaultValue={!!form.getValues().allZonas}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allZonas: value,
                zonas: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Sectores ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<Sector>
            label="Sectores"
            name="sectores"
            textFieldKey="nombre"
            valueKey="alias"
            actualValueKey="codigo"
            // options
            options={sectoresPagingRes?.data?.data || []}
            defaultValue={
              !!editPromocion?.sectores?.length
                ? sectoresPagingRes?.data?.data.filter((sector: Sector) => {
                    const sectoresDb = editPromocion?.sectores || [];

                    return sectoresDb.some(
                      (sectorDb: string) => sectorDb === sector?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingSectores}
            // errors
            control={form.control}
            error={!!errors.sectores?.length}
            helperText={errors.sectores?.message}
            disabled={!!watchedAllSectores}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allSectores"
            control={form.control}
            defaultValue={!!form.getValues().allSectores}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allSectores: value,
                sectores: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Planes ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<Plan>
            label="Planes"
            name="planes"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={planesRes?.data.data || []}
            defaultValue={
              !!editPromocion?.planes?.length
                ? planesRes?.data.data.filter((plan: Plan) => {
                    const planesDb = editPromocion?.planes || [];

                    return planesDb.some(
                      (planDb: string) => planDb === plan?.codigo
                    );
                  })
                : []
            }
            isLoadingData={isLoadingPlanes}
            // errors
            control={form.control}
            error={!!errors.planes?.length}
            helperText={errors.planes?.message}
            disabled={!!watchedAllPlanes}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allPlanes"
            control={form.control}
            defaultValue={!!form.getValues().allPlanes}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allPlanes: value,
                planes: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Centro de costo ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<CENTROCOSTO>
            label="Centro de costo"
            name="centros_costo"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={centroCostoRes?.data.data || []}
            defaultValue={
              !!editPromocion?.centros_costo?.length
                ? centroCostoRes?.data.data.filter(
                    (centroCosto: CENTROCOSTO) => {
                      const centrosCostoDb = editPromocion?.centros_costo || [];

                      return centrosCostoDb.some(
                        (centroCostoDb: string) =>
                          centroCostoDb === centroCosto?.codigo
                      );
                    }
                  )
                : []
            }
            isLoadingData={isLoadingCentroCosto}
            // errors
            control={form.control}
            error={!!errors.centros_costo?.length}
            helperText={errors.centros_costo?.message}
            disabled={!!watchedAllCentrosCosto}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allCentrosCosto"
            control={form.control}
            defaultValue={!!form.getValues().allCentrosCosto}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allCentrosCosto: value,
                centros_costo: allDataArr,
              });
            }}
          />
        </Grid>

        {/* ------ Canal Ventas ------ */}
        <Grid item container xs={12} spacing={3}>
          <AcutocompletePromocion<CanalVenta>
            label="Canales de venta"
            name="canales_venta"
            textFieldKey="nombre"
            valueKey="nombre"
            actualValueKey="codigo"
            // options
            options={canalesVentaRes?.data.data || []}
            defaultValue={
              !!editPromocion?.canales_venta?.length
                ? canalesVentaRes?.data.data.filter(
                    (canalVenta: CanalVenta) => {
                      const canalesVentaDb = editPromocion?.canales_venta || [];

                      return canalesVentaDb.some(
                        (canalVentaDb: string) =>
                          canalVentaDb === canalVenta?.codigo
                      );
                    }
                  )
                : []
            }
            isLoadingData={isLoadingCanalesVenta}
            // errors
            control={form.control}
            error={!!errors.canales_venta?.length}
            helperText={errors.canales_venta?.message}
            disabled={!!watchedAllCanalesVenta}
            size={gridSizeMdLg10}
            useSpecificKey
            specificKey="codigo"
          />
          <SampleCheckbox
            label="TODOS"
            name="allCanalesVenta"
            control={form.control}
            defaultValue={!!form.getValues().allCanalesVenta}
            size={gridSizeMdLg2}
            onChangeValue={value => {
              form.reset({
                ...form.getValues(),
                allCanalesVenta: value,
                canales_venta: allDataArr,
              });
            }}
          />

          {/* ----- Meses Gratuitos ----- */}
          <CustomAutocompleteMultipleArrString
            label="Meses Gratuitos"
            name="meses_gratuitos"
            textFieldKey="meses_gratuitos"
            control={form.control}
            defaultValue={
              !!editPromocion?.meses_gratuitos?.length
                ? editPromocion?.meses_gratuitos
                : []
            }
            // options
            options={MES_GRATIS_STRING}
            isLoadingData={false}
            // errors
            error={!!errors.meses_gratuitos?.length}
            helperText={errors.meses_gratuitos?.message}
            size={gridSizeMdLg6}
            enableStringify={false}
          />
          <CustomAutocompleteMultipleArrString
            label="Meses Descuento"
            name="meses_descuento"
            textFieldKey="meses_descuento"
            control={form.control}
            defaultValue={
              !!editPromocion?.meses_descuento?.length
                ? editPromocion?.meses_descuento
                : []
            }
            // options
            options={MES_GRATIS_STRING}
            isLoadingData={false}
            // errors
            error={!!errors.meses_descuento?.length}
            helperText={errors.meses_descuento?.message}
            size={gridSizeMdLg6}
            enableStringify={false}
          />

          <CustomAutocompleteArrString
            label="Recurrencia"
            name="recurrencia"
            control={form.control}
            defaultValue={form.getValues('recurrencia')}
            options={RECURRENCIAS_CHOICE}
            isLoadingData={false}
            error={errors.recurrencia}
            helperText={errors.recurrencia?.message}
            size={gridSizeMdLg6}
            disableClearable
          />
          <SampleCheckbox
            label="Estado"
            name="estado"
            control={form.control}
            defaultValue={!!form.getValues().estado}
            isState
            size={gridSizeMdLg6}
          />
        </Grid>
      </>

      {/* ============ date ============ */}
      <SampleDatePicker
        label="Fecha Inicio"
        name="fecha_inicio"
        control={form.control}
        defaultValue={form.getValues().fecha_inicio || null}
        error={errors.fecha_inicio}
        helperText={errors.fecha_inicio?.message}
        size={gridSizeMdLg6}
        onChangeRawValue={date => {
          setMinDate(date);
          form.setValue('fecha_fin', null as any);
        }}
      />
      <SampleDatePicker
        label="Fecha Fin"
        name="fecha_fin"
        control={form.control}
        defaultValue={form.getValues().fecha_fin || null}
        error={errors.fecha_fin}
        helperText={errors.fecha_fin?.message}
        size={gridSizeMdLg6}
        required={false}
        minDate={minDate}
        clearable
        onChangeRawValue={date => {
          if (!date) form.setValue('fecha_fin', null as any);
        }}
      />
    </SingleFormBoxScene>
  );
};

export default SavePromocion;
